import type { OnDestroy, AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  Inject,
  Optional,
  Input,
  ChangeDetectorRef,
  HostListener,
  HostBinding,
} from '@angular/core';
import type { FocusableOption, FocusOrigin } from '@angular/cdk/a11y';
import { FocusMonitor } from '@angular/cdk/a11y';
import type { CanDisableRipple, CanDisable } from '../../core/common';
import { mixinDisabled, mixinDisableRipple } from '../../core/common';
import { Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { MatMenuPanel, MAT_MENU_PANEL } from '../menu-panel';

// Boilerplate for applying mixins to MatMenuItem.
const _MatMenuItemBase = mixinDisableRipple(mixinDisabled(class {}));

/**
 * Single item inside a `mat-menu`. Provides the menu item styling and accessibility treatment.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-mat-menu-item]',
  exportAs: 'matMenuItem',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'menu-item.component.html',
})
export class MatMenuItemComponent
  extends _MatMenuItemBase
  implements FocusableOption, CanDisable, CanDisableRipple, AfterViewInit, OnDestroy
{
  @Input() override disabled: boolean;
  @Input() override disableRipple: boolean;

  /** ARIA role for the menu item. */
  @HostBinding('attr.role')
  @Input()
  role: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox' = 'menuitem';

  // Classes added by the base class that should be removed.
  @HostBinding('class')
  readonly hostClass = 'mat-menu-item mat-focus-indicator';

  /** Whether the menu item is highlighted. */
  @HostBinding('class.mat-menu-item-highlighted')
  _highlighted: boolean = false;

  /** Whether the menu item acts as a trigger for a sub-menu. */
  @HostBinding('class.mat-menu-item-submenu-trigger')
  _triggersSubmenu: boolean = false;

  @HostBinding('attr.aria-disabled')
  get ariaDisabled(): string {
    return this.disabled.toString();
  }

  @HostBinding('attr.disabled')
  get disabledAttr() {
    return this.disabled || null;
  }

  /** Stream that emits when the menu item is hovered. */
  readonly _hovered: Subject<MatMenuItemComponent> = new Subject<MatMenuItemComponent>();

  /** Stream that emits when the menu item is focused. */
  readonly _focused = new Subject<MatMenuItemComponent>();

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _focusMonitor: FocusMonitor,
    private _changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private _document?: any,
    @Inject(MAT_MENU_PANEL) @Optional() public _parentMenu?: MatMenuPanel,
  ) {
    super();
  }

  /** Used to set the `tabindex`. */
  @HostBinding('attr.tabindex')
  _getTabIndex(): string {
    return this.disabled ? '-1' : '0';
  }

  /** Prevents the default element actions if it is disabled. */
  @HostListener('click', ['$event'])
  _checkDisabled(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /** Emits to the hover stream. */
  @HostListener('mouseenter')
  _handleMouseEnter() {
    this._hovered.next(this);
  }

  /** Focuses the menu item. */
  focus(origin?: FocusOrigin, options?: FocusOptions): void {
    if (this._focusMonitor && origin) {
      this._focusMonitor.focusVia(this._getHostElement(), origin, options);
    } else {
      this._getHostElement().focus(options);
    }

    this._focused.next(this);
  }

  ngAfterViewInit() {
    if (this._focusMonitor) {
      // Start monitoring the element, so it gets the appropriate focused classes. We want
      // to show the focus style for menu items only when the focus was not caused by a
      // mouse or touch interaction.
      this._focusMonitor.monitor(this._elementRef, false);
    }
  }

  ngOnDestroy() {
    if (this._focusMonitor) {
      this._focusMonitor.stopMonitoring(this._elementRef);
    }

    this._hovered.complete();
    this._focused.complete();
  }

  /** Returns the host DOM element. */
  _getHostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  /** Gets the label to be used when determining whether the option should be focused. */
  getLabel(): string {
    const clone = this._elementRef.nativeElement.cloneNode(true) as HTMLElement;
    const icons = clone.querySelectorAll('mat-icon, .material-icons');

    // Strip away icons, so they don't show up in the text.
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < icons.length; i++) {
      icons[i].remove();
    }

    return clone.textContent?.trim() || '';
  }

  _setHighlighted(isHighlighted: boolean) {
    // We need to mark this for check for the case where the content is coming from a
    // `matMenuContent` whose change detection tree is at the declaration position,
    // not the insertion position. See #23175.
    this._highlighted = isHighlighted;
    this._changeDetectorRef.markForCheck();
  }

  _setTriggersSubmenu(triggersSubmenu: boolean) {
    this._triggersSubmenu = triggersSubmenu;
    this._changeDetectorRef.markForCheck();
  }

  _hasFocus(): boolean {
    return this._document && this._document.activeElement === this._getHostElement();
  }
}
